import type {
  FourBeatChapterContract,
  FourBeatResponseFormat,
  GeneratedChapterBeat,
  GeneratedFourBeatChapter,
} from '../types';
import { countChapterWords } from './chapterExpansion.service';
import { toGeneratedChapterBeat } from './fourBeatChapterValidation.service';
import { FOUR_BEAT_AUTHORITATIVE_IDS } from './scribeThinkingPolicy.service';

export const FOUR_BEAT_SEPARATOR = '\n\n***\n\n';

export function assembleFourBeatChapter(params: {
  chapterId: number;
  title: string;
  summary: string;
  beats: GeneratedChapterBeat[];
  contract?: FourBeatChapterContract;
}): GeneratedFourBeatChapter {
  const sorted = [...params.beats].sort((a, b) => a.sequence - b.sequence);
  const content = sorted.map((beat) => beat.content.trim()).filter(Boolean).join(FOUR_BEAT_SEPARATOR);

  return {
    chapterId: params.chapterId,
    title: params.title,
    summary: params.summary,
    beats: sorted,
    content,
    totalWordCount: countChapterWords(content),
  };
}

type RawBeatPayload = {
  beatId?: string;
  sequence?: number;
  content?: string;
  endingState?: string;
  requiredEventsSatisfied?: string[];
  decisionPresent?: boolean;
  consequencePresent?: boolean;
};

export function tryRecoverBeatsFromRawResponse(json: {
  beats?: RawBeatPayload[];
  scenes?: string[];
}): GeneratedChapterBeat[] | null {
  if (json.beats && Array.isArray(json.beats) && json.beats.length === 4) {
    const recoverable = json.beats.every((beat) => Boolean((beat.content || '').trim()));
    if (!recoverable) return null;
    return json.beats.map((beat, index) =>
      toGeneratedChapterBeat({
        beatId: beat.beatId || FOUR_BEAT_AUTHORITATIVE_IDS[index],
        sequence: (beat.sequence || index + 1) as number,
        content: beat.content,
        endingState: beat.endingState,
        requiredEventsSatisfied: beat.requiredEventsSatisfied,
        decisionPresent: beat.decisionPresent,
        consequencePresent: beat.consequencePresent,
      }),
    );
  }
  return null;
}

export function parseFourBeatResponseV1(
  chapterId: number,
  json: {
    title?: string;
    summary?: string;
    beats?: RawBeatPayload[];
    scenes?: string[];
    content?: string;
  },
  responseMeta?: { finishReason?: string; outputTokenCount?: number },
): { ok: true; generated: GeneratedFourBeatChapter } | { ok: false; reason: string; recoverableBeats: GeneratedChapterBeat[] | null } {
  if (!json.beats || !Array.isArray(json.beats) || json.beats.length === 0) {
    const recoverableBeats = tryRecoverBeatsFromRawResponse(json);
    return {
      ok: false,
      reason: 'FOUR_BEAT_V1 response missing beats[] array.',
      recoverableBeats,
    };
  }

  if (json.beats.length !== 4) {
    return {
      ok: false,
      reason: `FOUR_BEAT_V1 response returned ${json.beats.length} beats; expected exactly 4.`,
      recoverableBeats: tryRecoverBeatsFromRawResponse(json),
    };
  }

  const beats = json.beats.map((beat, index) =>
    toGeneratedChapterBeat({
      beatId: beat.beatId || FOUR_BEAT_AUTHORITATIVE_IDS[index],
      sequence: beat.sequence || index + 1,
      content: beat.content,
      endingState: beat.endingState,
      requiredEventsSatisfied: beat.requiredEventsSatisfied,
      decisionPresent: beat.decisionPresent,
      consequencePresent: beat.consequencePresent,
      finishReason: responseMeta?.finishReason,
      outputTokenCount: responseMeta?.outputTokenCount,
      truncated: responseMeta?.finishReason === 'MAX_TOKENS',
    }),
  );

  const generated = assembleFourBeatChapter({
    chapterId,
    title: json.title || `Chapter ${chapterId}`,
    summary: json.summary || '',
    beats,
  });

  return { ok: true, generated };
}

export function parseLegacyScenesImport(
  chapterId: number,
  json: {
    title?: string;
    summary?: string;
    scenes?: string[];
  },
  responseMeta?: { finishReason?: string; outputTokenCount?: number },
): GeneratedFourBeatChapter {
  if (!json.scenes || !Array.isArray(json.scenes)) {
    throw new Error('LEGACY_SCENES_IMPORT requires scenes[] array.');
  }

  const beats = json.scenes.map((scene, index) =>
    toGeneratedChapterBeat({
      beatId: FOUR_BEAT_AUTHORITATIVE_IDS[index] || `beat-${index + 1}`,
      sequence: index + 1,
      content: scene,
      endingState: '',
      finishReason: responseMeta?.finishReason,
      outputTokenCount: responseMeta?.outputTokenCount,
      truncated: responseMeta?.finishReason === 'MAX_TOKENS',
    }),
  );

  return assembleFourBeatChapter({
    chapterId,
    title: json.title || `Chapter ${chapterId}`,
    summary: json.summary || '',
    beats,
  });
}

/** @deprecated Use parseFourBeatResponseV1 or parseLegacyScenesImport explicitly. */
export function parseFourBeatResponse(
  chapterId: number,
  json: {
    title?: string;
    summary?: string;
    beats?: RawBeatPayload[];
    scenes?: string[];
    content?: string;
  },
  responseMeta?: { finishReason?: string; outputTokenCount?: number },
  responseFormat: FourBeatResponseFormat = 'FOUR_BEAT_V1',
): GeneratedFourBeatChapter {
  if (responseFormat === 'LEGACY_SCENES_IMPORT') {
    return parseLegacyScenesImport(chapterId, json, responseMeta);
  }

  const parsed = parseFourBeatResponseV1(chapterId, json, responseMeta);
  if (parsed.ok) return parsed.generated;
  if (parsed.recoverableBeats) {
    return assembleFourBeatChapter({
      chapterId,
      title: json.title || `Chapter ${chapterId}`,
      summary: json.summary || '',
      beats: parsed.recoverableBeats,
    });
  }
  throw new Error(parsed.reason);
}

export function buildFourBeatAssemblyDiagnostic(generated: GeneratedFourBeatChapter) {
  return {
    chapterId: generated.chapterId,
    beatCount: generated.beats.length,
    beatIds: generated.beats.map((beat) => beat.beatId),
    beatSequences: generated.beats.map((beat) => beat.sequence),
    beatWordCounts: generated.beats.map((beat) => beat.wordCount),
    totalWordCount: generated.totalWordCount,
  };
}
